import { useEffect, useRef, useState } from 'react';

import usePreviousRoute from './usePreviousRoute';
import { routePathAd } from 'src/constants/router';
import type { Ad } from 'src/types/__generated__/graphql';

type ScrollRestorationParams = { delegateExecution?: boolean; scrollRestorationQueryKey: string };

export const useAdScrollRestoration = <T extends HTMLElement>({
    delegateExecution = true,
    scrollRestorationQueryKey,
}: ScrollRestorationParams) => {
    const { previousQuery, previousPathname } = usePreviousRoute();
    const rawRef = useRef<null | T>(null);

    const shouldRestoreScroll = Boolean(
        previousQuery?.[scrollRestorationQueryKey] && previousPathname === `${routePathAd}/[adId]`,
    );
    const [hasRestoredScrollPos, setHasRestoredScrollPos] = useState<boolean>(false);

    const restoreScroll = (): boolean => {
        if (shouldRestoreScroll && rawRef.current?.scrollIntoView && !hasRestoredScrollPos) {
            rawRef.current.scrollIntoView();
            setHasRestoredScrollPos(true);
            return true;
        }
        return false;
    };
    useEffect(() => {
        if (delegateExecution) {
            restoreScroll();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- should only happen on mount; if you need the caller to control this then pass `{delegateExecution: false}`
    }, []);
    return {
        shouldRestoreScroll,
        restoreScroll,
        scrollRestorationRef: (ad: Pick<Ad, 'id'>) =>
            shouldRestoreScroll && previousQuery?.adId === ad.id ? rawRef : undefined,
    };
};
